import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

const GET_RECENT_INQUIRIES = gql`
  query GetRecentInquiries {
    listInquiries(limit: 10) {
      items {
        id
        firstName
        lastName
        status
        createdAt
        activityLog {
          id
          activityType
          performedByName
          timestamp
          details
        }
      }
    }
  }
`;

interface ActivityLogItem {
  id: string;
  activityType: string;
  performedByName?: string;
  timestamp: string;
  details?: string;
}

interface InquiryItem {
  id: string;
  firstName: string;
  lastName: string;
  status: string;
  createdAt: string;
  activityLog?: ActivityLogItem[];
}

interface ListInquiriesData {
  listInquiries: {
    items: InquiryItem[];
  };
}

export function RecentActivityWidget() {
  const { data, loading, error } = useQuery<ListInquiriesData>(GET_RECENT_INQUIRIES, {
    fetchPolicy: 'cache-and-network',
  });

  const inquiries = data?.listInquiries?.items || [];
  
  // Flatten all activities from all inquiries and sort by timestamp
  const allActivities = inquiries
    .flatMap((inquiry: any) => 
      (inquiry.activityLog || []).map((activity: any) => ({
        ...activity,
        inquiryName: `${inquiry.firstName} ${inquiry.lastName}`,
        inquiryId: inquiry.id,
      }))
    )
    .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 8);

  const getActivityIcon = (type: string) => {
    const icons: Record<string, string> = {
      SUBMITTED: '📝',
      VIEWED: '👁️',
      STATUS_CHANGED: '🔄',
      CONTACTED_CLIENT: '📞',
      CLIENT_RESPONDED: '💬',
      NOTE_ADDED: '📌',
      ATTACHMENT_ADDED: '📎',
      ASSIGNED: '👤',
      CONVERTED: '✅',
      REJECTED: '❌',
    };
    return icons[type] || '•';
  };

  const getActivityColor = (type: string) => {
    const colors: Record<string, string> = {
      SUBMITTED: 'text-blue-600',
      VIEWED: 'text-gray-600',
      STATUS_CHANGED: 'text-orange-600',
      CONTACTED_CLIENT: 'text-purple-600',
      CLIENT_RESPONDED: 'text-green-600',
      NOTE_ADDED: 'text-yellow-600',
      ATTACHMENT_ADDED: 'text-indigo-600',
      ASSIGNED: 'text-teal-600',
      CONVERTED: 'text-green-700',
      REJECTED: 'text-red-600',
    };
    return colors[type] || 'text-gray-600';
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600 text-sm">Error loading activity</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="inquiry-card">
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-96 overflow-y-auto space-y-3">
          {allActivities.length === 0 ? (
            <p className="text-center text-gray-500 py-4 text-sm">No recent activity</p>
          ) : (
            allActivities.map((activity: any) => (
              <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                <span className="text-xl">{getActivityIcon(activity.activityType)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-semibold">{activity.inquiryName}</span>
                    {' • '}
                    <span className={getActivityColor(activity.activityType)}>
                      {activity.activityType.replace(/_/g, ' ')}
                    </span>
                  </p>
                  <p className="text-xs text-gray-600 mt-1 truncate">{activity.details}</p>
                  {activity.performedByName && (
                    <p className="text-xs text-gray-500 mt-1">
                      by {activity.performedByName}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">{formatTimestamp(activity.timestamp)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
